/**
 * Node Config Panel Component
 *
 * Unified, data-driven side panel for editing selected node properties.
 * Uses ConfigSection for declarative config field rendering based on
 * NodeTypeSpec.config_fields from the backend.
 *
 * Structure:
 * - Header (node type icon and label)
 * - Label input
 * - Input Fields Section (mappable inputs from upstream)
 * - Config Section (data-driven from API)
 * - Output Fields Section (outputs for downstream mapping)
 * - Execution Results (when node has been executed)
 * - Footer Actions (duplicate/delete)
 */

import { CheckCircle2, Image, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { parseBackendDateTime } from '@/lib/format/datetime';
import { nodeConfig } from './nodes';
import {
  useWorkflowStore,
  useSelectedNode,
  useNodeExecutionResult,
  useNodeExecutionStatus,
  useIsExecuting,
} from '../hooks/useWorkflowStore';
import type { NodeType, NodeData, CustomVariable, BuiltInVariables, StepStatus } from '@/types/imageWorkflow';
import { InputFieldsSection } from './InputFieldsSection';
import { OutputFieldsSection } from './OutputFieldsSection';
import { ConfigSection } from './ConfigSection';
import { useNodeFields } from '../hooks/useNodeFields';
import { useI18n } from '@/i18n';

// Built-in variable keys for Start node
const BUILTIN_VARIABLE_KEYS: (keyof BuiltInVariables)[] = [
  'user_id', 'user_name', 'user_email', 'execution_id', 'workflow_id', 'workflow_name', 'timestamp', 'date', 'time'
];

// Hook to get localized builtin variables
function useBuiltinVariables() {
  const { t } = useI18n();
  return BUILTIN_VARIABLE_KEYS.map((key) => ({
    key,
    label: t(`imageWorkflows.nodeConfigPanel.builtinVariables.${key}.label`),
    description: t(`imageWorkflows.nodeConfigPanel.builtinVariables.${key}.description`),
  }));
}

interface NodeConfigPanelProps {
  onClose?: () => void;
}

// Hook to get localized execution status labels
function useExecutionStatusLabels(): Record<StepStatus, { label: string; color: string }> {
  const { t } = useI18n();
  return {
    pending: { label: t('imageWorkflows.nodeConfigPanel.status.pending'), color: 'text-gray-500' },
    running: { label: t('imageWorkflows.nodeConfigPanel.status.running'), color: 'text-yellow-500' },
    completed: { label: t('imageWorkflows.nodeConfigPanel.status.completed'), color: 'text-green-500' },
    failed: { label: t('imageWorkflows.nodeConfigPanel.status.failed'), color: 'text-red-500' },
    skipped: { label: t('imageWorkflows.nodeConfigPanel.status.skipped'), color: 'text-gray-400' },
  };
}

// Node type to color mapping
const NODE_COLORS: Record<NodeType, { border: string; bg: string }> = {
  start: { border: 'border-cyan-200', bg: 'bg-cyan-50/50' },
  prompt: { border: 'border-purple-200', bg: 'bg-purple-50/50' },
  generate: { border: 'border-pink-200', bg: 'bg-pink-50/50' },
  edit: { border: 'border-orange-200', bg: 'bg-orange-50/50' },
  fuse: { border: 'border-indigo-200', bg: 'bg-indigo-50/50' },
  lucky_charm: { border: 'border-amber-200', bg: 'bg-amber-50/50' },
  push: { border: 'border-rose-200', bg: 'bg-rose-50/50' },
  output: { border: 'border-blue-200', bg: 'bg-blue-50/50' },
  read_today_artifact: { border: 'border-sky-200', bg: 'bg-sky-50/50' },
};

// Node types that have input fields section
const NODES_WITH_INPUTS: NodeType[] = ['prompt', 'generate', 'edit', 'fuse', 'lucky_charm', 'push', 'output'];

// Node types that have output fields section
// Note: 'push' is a terminal node - no outputs needed (results shown in execution panel)
const NODES_WITH_OUTPUTS: NodeType[] = ['start', 'prompt', 'generate', 'edit', 'fuse', 'lucky_charm'];

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({ onClose }) => {
  const { t } = useI18n();
  const selectedNode = useSelectedNode();
  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);
  const deleteNode = useWorkflowStore((s) => s.deleteNode);
  const duplicateNode = useWorkflowStore((s) => s.duplicateNode);
  const selectNode = useWorkflowStore((s) => s.selectNode);

  // Check if workflow is executing (disable editing during execution)
  const isExecuting = useIsExecuting();

  // Get localized data
  const BUILTIN_VARIABLES = useBuiltinVariables();
  const executionStatusLabels = useExecutionStatusLabels();

  // Execution state for selected node (use nodeKey instead of id)
  const nodeKey = selectedNode?.data?.nodeKey || selectedNode?.id?.replace(/^rf-/, '') || '';
  const executionStatus = useNodeExecutionStatus(nodeKey);
  const executionResult = useNodeExecutionResult(nodeKey);

  // Field mapping for source toggling (all node types)
  const {
    inputMappings,
    customInputFields,
    schemaOutputFields,
    availableSources,
    updateMapping,
    updateMappingKeyPath,
    removeMapping,
  } = useNodeFields(selectedNode?.id || null);

  // Get all nodes to find Start node for context/variables
  const nodes = useWorkflowStore((s) => s.nodes);

  // Find Start node and extract context/variables
  const startNode = nodes.find((n) => n.type === 'start');
  const startNodeData = startNode?.data as NodeData | undefined;

  // Build context variables from Start node's enabled builtins
  const contextVariables = (startNodeData?.enabled_builtins || []).map((key) => {
    const builtin = BUILTIN_VARIABLES.find((b) => b.key === key);
    return {
      key,
      label: builtin?.label || key,
      type: 'string' as const,
    };
  });

  // Build start variables from Start node's custom variables
  const startVariables = (startNodeData?.variables || [])
    .filter((v: CustomVariable) => v.key && v.key.trim())
    .map((v: CustomVariable) => ({
      key: v.key,
      value: v.value,
    }));

  // Current node's custom input fields for FieldSourceToggle
  // Include object_keys from upstream source if the input is mapped to an object-type output
  const currentCustomInputs = customInputFields.map((f) => {
    // Find the mapping for this input field
    const mapping = inputMappings.find((m) => m.input_field_key === f.key);
    let objectKeys = undefined;

    if (mapping && f.type === 'object') {
      // Find the upstream source and get its object_keys
      const source = availableSources.find((s) => s.node_key === mapping.source_node_key);
      if (source) {
        const output = source.outputs.find((o) => o.key === mapping.source_output_key);
        objectKeys = output?.object_keys;
      }
    }

    return {
      key: f.key,
      type: f.type,
      label: f.label,
      object_keys: objectKeys,
    };
  });

  // Empty state
  if (!selectedNode) {
    return (
      <div className="w-[420px] border-l bg-white p-4">
        <div className="text-center text-gray-400 mt-20">
          <svg
            className="w-12 h-12 mx-auto mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
            />
          </svg>
          <p className="text-sm">{t('imageWorkflows.nodeConfigPanel.selectNodePrompt')}</p>
        </div>
      </div>
    );
  }

  const nodeTypeValue = selectedNode.type as NodeType;
  const nodeConfigInfo = nodeConfig[nodeTypeValue];
  const data = selectedNode.data as NodeData;
  const colors = NODE_COLORS[nodeTypeValue];

  const handleUpdate = (updates: Partial<NodeData>) => {
    updateNodeData(selectedNode.id, updates);
  };

  const handleDelete = () => {
    deleteNode(selectedNode.id);
  };

  const handleDuplicate = () => {
    duplicateNode(selectedNode.id);
  };

  const handleClose = () => {
    selectNode(null);
    onClose?.();
  };

  // Check if this node type has inputs/outputs
  const hasInputs = NODES_WITH_INPUTS.includes(nodeTypeValue);
  const hasOutputs = NODES_WITH_OUTPUTS.includes(nodeTypeValue);

  return (
    <div className="w-[420px] border-l bg-white flex flex-col max-h-screen">
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${nodeConfigInfo.bgColor}`}
      >
        <div className="flex items-center gap-2">
          <span className={nodeConfigInfo.color}>{nodeConfigInfo.icon}</span>
          <span className={`font-medium ${nodeConfigInfo.color}`}>
            {nodeConfigInfo.label} Node
          </span>
        </div>
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Label */}
        <Input
          label={t('imageWorkflows.nodeConfigPanel.labelInput')}
          value={data.label || ''}
          onChange={(e) => handleUpdate({ label: e.target.value })}
          placeholder={t('imageWorkflows.nodeConfigPanel.labelPlaceholder')}
          disabled={isExecuting}
        />

        {/* Input Fields Section (for nodes that have inputs) */}
        {hasInputs && (
          <InputFieldsSection
            nodeId={selectedNode.id}
            isDisabled={isExecuting}
            title={t('imageWorkflows.nodeConfigPanel.inputFields')}
            borderColorClass={colors.border}
            bgColorClass={colors.bg}
            contextVariables={contextVariables}
            startVariables={startVariables}
          />
        )}

        {/* Config Section - Data-driven from API */}
        <ConfigSection
          nodeType={nodeTypeValue}
          nodeData={data}
          onUpdate={handleUpdate}
          isDisabled={isExecuting}
          inputMappings={inputMappings}
          updateMapping={updateMapping}
          updateKeyPath={updateMappingKeyPath}
          removeMapping={removeMapping}
          availableSources={[]}
          customInputs={currentCustomInputs}
          contextVariables={contextVariables}
          startVariables={startVariables}
        />

        {/* Output Fields Section (for nodes that have outputs) */}
        {hasOutputs && (
          <OutputFieldsSection
            nodeId={selectedNode.id}
            isDisabled={isExecuting}
            title={t('imageWorkflows.nodeConfigPanel.outputFields')}
            borderColorClass={colors.border.replace('border-', 'border-').replace('-200', '-200')}
            bgColorClass={colors.bg.replace('/50', '/30')}
            customInputFields={currentCustomInputs}
            schemaOutputFields={schemaOutputFields}
          />
        )}

        {/* Execution Results */}
        {executionStatus && (
          <div className="mt-4 p-4 border-2 border-gray-200 rounded-lg bg-gray-50 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">{t('imageWorkflows.nodeConfigPanel.executionResult')}</span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  executionStatus === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : executionStatus === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : executionStatus === 'running'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                }`}
              >
                {executionStatusLabels[executionStatus]?.label}
              </span>
            </div>

            {/* Execution timing */}
            {executionResult && (
              <div className="text-xs text-gray-500 space-y-1">
                {executionResult.started_at && (
                  <p>{t('imageWorkflows.nodeConfigPanel.timing.started')}: {parseBackendDateTime(executionResult.started_at).toLocaleTimeString()}</p>
                )}
                {executionResult.completed_at && (
                  <p>{t('imageWorkflows.nodeConfigPanel.timing.completed')}: {parseBackendDateTime(executionResult.completed_at).toLocaleTimeString()}</p>
                )}
                {executionResult.duration_ms && (
                  <p>{t('imageWorkflows.nodeConfigPanel.timing.duration')}: {(executionResult.duration_ms / 1000).toFixed(2)}s</p>
                )}
              </div>
            )}

            {/* Output data */}
            {executionResult?.output_data &&
              Object.keys(executionResult.output_data).length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-gray-600">{t('imageWorkflows.nodeConfigPanel.output.title')}:</span>

                  {/* Show generated prompt */}
                  {Boolean(executionResult.output_data.prompt) && (
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500 mb-1">{t('imageWorkflows.nodeConfigPanel.output.prompt')}:</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {String(executionResult.output_data.prompt)}
                      </p>
                    </div>
                  )}

                  {/* Show generated image */}
                  {Boolean(executionResult.output_data.image_url) && (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Image className="w-3 h-3" />
                        {t('imageWorkflows.nodeConfigPanel.output.generatedImage')}:
                      </p>
                      <img
                        src={String(executionResult.output_data.image_url)}
                        alt="Generated"
                        className="w-full rounded border shadow-sm"
                      />
                    </div>
                  )}

                  {/* Show file_id if saved to library */}
                  {Boolean(executionResult.output_data.file_id) && (
                    <div className="p-2 bg-green-50 rounded border border-green-200">
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {t('imageWorkflows.nodeConfigPanel.output.savedToLibrary')}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('imageWorkflows.nodeConfigPanel.output.fileId')}:{' '}
                        <code className="bg-green-100 px-1 rounded">
                          {String(executionResult.output_data.file_id)}
                        </code>
                      </p>
                    </div>
                  )}

                  {/* Show other output fields (e.g., schema-derived fields) */}
                  {Object.entries(executionResult.output_data)
                    .filter(([key]) => !['prompt', 'image_url', 'file_id'].includes(key))
                    .map(([key, value]) => (
                      <div key={key} className="p-2 bg-white rounded border">
                        <p className="text-xs text-gray-500 mb-1 font-medium">{key}:</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap break-all">
                          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                        </p>
                      </div>
                    ))}
                </div>
              )}

            {/* Error message */}
            {executionResult?.error_message && (
              <div className="p-2 bg-red-50 rounded border border-red-200">
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  {t('imageWorkflows.nodeConfigPanel.error')}:
                </p>
                <p className="text-sm text-red-700 mt-1">{executionResult.error_message}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer actions - hidden for Start node (cannot duplicate or delete) */}
      {nodeTypeValue !== 'start' && (
        <div className="border-t p-4 flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={handleDuplicate}
            disabled={isExecuting}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {t('imageWorkflows.nodeConfigPanel.duplicate')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="flex-1"
            onClick={handleDelete}
            disabled={isExecuting}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            {t('imageWorkflows.nodeConfigPanel.delete')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NodeConfigPanel;
